import { CSSResultGroup, TemplateResult, html } from 'lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
import {
  customElement,
  property,
  queryAssignedElements,
} from 'lit/decorators.js';
import componentStyles from './sic-tabs.css.lit';
import { SicTabPanel } from './sic-tab-panel/sic-tab-panel';
import { screenSizeMap } from '../../controllers/screen-size-controller';
import { debounce } from '../../../utilities/debounce';

/**
 * The SicTabs component
 * @element sic-tabs
 * @slot - tabs: Slot for sic--tab-panel components.
 */
@customElement('sic-tabs')
export class SicTabs extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener(
      'resize',
      debounce(() => this.handleMobile(), 100)
    );
    this.isMobile = this.handleMobile();
  }

  disconnectedCallback(): void {
    window.removeEventListener(
      'resize',
      debounce(() => this.handleMobile(), 100)
    );
  }

  /**
   * Slot for SicTabPanel components
   */
  @queryAssignedElements({ slot: 'tabs' })
  tabs: Array<HTMLElement>;

  /**
   * Array of tab titles
   */
  @property({ type: Array })
  listItems: Array<string> = [];

  @property({ type: String, reflect: true })
  id = 'tabs-nav';

  @property({ type: Boolean })
  isMobile: boolean;

  firstUpdated() {
    // Cycle through slotted tab-panels,
    // add accessibility attributes.
    this.tabs.forEach((panel, i) => {
      const tabEL = panel as SicTabPanel;
      tabEL.setAttribute('id', `panel-${i + 1}`);
      tabEL.setAttribute('aria-labelledby', `tab-${i + 1}`);

      this.listItems.push(tabEL.tabTitle);
    });
    // generate markup on load and then check for applicable hash in the url on load.
    this.generateListMarkup().then(() => this.checkForActiveHashOnLoad());
  }

  updated() {
    this.handleMobileSwap();
  }

  render(): TemplateResult {
    return html`
      <div class="sic-tabs">
        <div class="sic-tabs--list-wrapper">
          <ul class="sic-tabs--list" role="tablist"></ul>
        </div>
        <div aria-atomic="true" aria-live="polite" class="sic-tabs--tabs">
          <slot name="tabs"></slot>
        </div>
      </div>
    `;
  }

  setContentActive(e?: Event, targetEL?: HTMLAnchorElement) {
    const clicked = targetEL ? targetEL : (e?.target as HTMLAnchorElement);
    const clickedHref = clicked?.getAttribute('href');
    const self = document.querySelector(`#tabs-nav`) as SicTabs;

    if (self) {
      // set all tabs to aria-selected=false
      self.removeAllTabAriaSelected();
      // remove active attribute
      self.removeAllPanelActive();

      const elToSetActive = document?.querySelector(`${clickedHref}`);
      if (elToSetActive) {
        elToSetActive.setAttribute('active', 'true');
        clicked.setAttribute('aria-selected', 'true');
        clicked.setAttribute('tabindex', '0');
      }
    }
  }

  async generateListMarkup() {
    const listDiv = this.shadowRoot?.querySelector(
      '.sic-tabs--list'
    ) as HTMLElement;
    this.listItems.map((item, i) => {
      const li = document.createElement('li');
      const a = document.createElement('a');

      a.classList.add('sic-tabs--list-item');
      a.setAttribute('href', `#panel-${i + 1}`);
      a.setAttribute('id', `tab-${i + 1}`);
      a.setAttribute('role', 'tab');
      a.setAttribute('tabindex', '-1');
      a.innerText = item;
      a.addEventListener('click', e => this.tabSelected(e));
      a.addEventListener('keydown', e => this.handleKeyDown(this, e));
      li.appendChild(a);
      li.setAttribute('role', 'presentation');
      listDiv.appendChild(li);

      if (i === 0) {
        a.setAttribute('tabindex', '0');
        if (!this.isMobile) {
          this.setContentActive(undefined, a);
        }
      }
    });
  }

  /**
   * Checks the url for a hash value. If so compares that to link hrefs
   * and if a match is found sets that link to active.
   */
  checkForActiveHashOnLoad() {
    const urlHasHash = window.location.hash !== '';

    if (urlHasHash) {
      const hashValue = window.location.hash;
      const links = this.shadowRoot?.querySelectorAll('.sic-tabs--list-item');
      if (links?.length) {
        const tabToSetActive = [...links].filter(
          link => link.getAttribute('href') === hashValue
        )[0] as HTMLAnchorElement;
        if (tabToSetActive) {
          this.setContentActive(undefined, tabToSetActive);
        }
      }
    }
  }

  handleRightArrow(labelText: string) {
    const listItemPosition = this.listItems.indexOf(labelText) + 1;
    const count = this.tabs.length;

    if (listItemPosition < count) {
      const elFromIndex = this.tabs[listItemPosition];
      const nextTabID = elFromIndex.getAttribute('aria-labelledby');

      const nexSibling = this.shadowRoot?.querySelector(
        `#${nextTabID}`
      ) as HTMLAnchorElement;
      nexSibling.focus();
    }

    if (listItemPosition >= count) {
      const firstElement = this.shadowRoot?.querySelector(
        `#${this.tabs[0]?.getAttribute('aria-labelledby')}`
      ) as HTMLAnchorElement;
      if (firstElement) {
        firstElement.focus();
      }
    }
  }

  handleLeftArrow(labelText: string) {
    const listItemPosition = this.listItems.indexOf(labelText) + 1;
    const count = this.tabs.length;

    if (listItemPosition - 2 < 0) {
      const lastElement = this.shadowRoot?.querySelector(
        `#${this.tabs[count - 1]?.getAttribute('aria-labelledby')}`
      ) as HTMLAnchorElement;
      lastElement.focus();
    }

    if (listItemPosition - 2 >= 0) {
      const elFromIndex = this.tabs[listItemPosition - 2];
      const prevTabID = elFromIndex.getAttribute('aria-labelledby');

      const prevSibling = this.shadowRoot?.querySelector(
        `#${prevTabID}`
      ) as HTMLAnchorElement;
      prevSibling.focus();
    }
  }

  handleKeyDown(self: SicTabs, e: KeyboardEvent) {
    const targetEL = e.target as HTMLElement;
    if (e.key === 'ArrowRight') {
      self.handleRightArrow(targetEL.innerText);
    }
    if (e.key === 'ArrowLeft') {
      self.handleLeftArrow(targetEL.innerText);
    }
    if (e.key === ' ' || e.key === 'Enter') {
      this.tabSelected(e);
    }
  }

  /**
   * When tab is selected set the url hash, active tab, focus
   */
  tabSelected(e: Event) {
    e.preventDefault();
    const anchorEl = e.target as HTMLAnchorElement;
    const tabHash = anchorEl.getAttribute('href');
    this.setContentActive(undefined, anchorEl);

    // Set hash
    const url = new URL(location.href);
    if (tabHash) {
      url.hash = tabHash;
      history.pushState({}, '', url.toString());
    }
  }

  removeAllTabAriaSelected() {
    this.shadowRoot?.querySelectorAll('.sic-tabs--list-item').forEach(el => {
      el.removeAttribute('aria-selected');
      el.setAttribute('tabindex', '-1');
    });
  }

  removeAllPanelActive() {
    this.querySelectorAll('sic-tab-panel').forEach(el =>
      el.removeAttribute('active')
    );
  }

  handleMobile() {
    return (this.isMobile =
      window.innerWidth <= Number(screenSizeMap.get('lg')));
  }

  closeAll() {
    this.removeAllPanelActive();
    this.removeAllTabAriaSelected();
  }

  setFirstTabActive() {
    const firstTabAnchor = this.shadowRoot?.querySelector(
      '.sic-tabs--list-item'
    ) as HTMLAnchorElement;
    this.setContentActive(undefined, firstTabAnchor);
  }

  setFirstTabIndex() {
    const firstTabAnchor = this.shadowRoot?.querySelector(
      '.sic-tabs--list-item'
    ) as HTMLAnchorElement;
    firstTabAnchor.setAttribute('tabindex', '0');
  }

  handleMobileSwap() {
    if (this.isMobile) {
      this.closeAll();
      this.setFirstTabIndex();
    } else {
      this.setFirstTabActive();
    }
  }
}
